import React, { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';

const TZ = 'America/Bogota';
const dayStr = (offset = 0) => new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(new Date(Date.now() + offset * 86400000));

const PRESETS = [
  { key: 'hoy', label: 'Hoy', desde: () => dayStr(0) },
  { key: '7d', label: '7 días', desde: () => dayStr(-6) },
  { key: '30d', label: '30 días', desde: () => dayStr(-29) },
];

/** Segundos → "45 s", "12 min", "3 h 5 min", "2 d 4 h". */
function dur(s) {
  if (s == null) return '—';
  if (s < 60) return `${s} s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h} h${m % 60 ? ` ${m % 60} min` : ''}`;
  return `${Math.floor(h / 24)} d ${h % 24} h`;
}

/**
 * Punto 2: descarga en Excel de los casos que empiezan en el rango elegido arriba,
 * con filtro opcional por asesor y etiqueta. El archivo lo arma el backend.
 */
function CaseReport({ desde, hasta }) {
  const { apiFetch } = useApi();
  const [people, setPeople] = useState([]);
  const [tags, setTags] = useState([]);
  const [userId, setUserId] = useState('');
  const [tagId, setTagId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    apiFetch('/api/admin/users').then(setPeople).catch(() => {});
    apiFetch('/api/tags').then(setTags).catch(() => {});
  }, [apiFetch]);

  async function download() {
    setBusy(true);
    setError('');
    try {
      const qs = new URLSearchParams({ desde, hasta });
      if (userId) qs.set('user_id', userId);
      if (tagId) qs.set('tag_id', tagId);
      const { blob, name } = await apiFetch(`/api/admin/reports/cases.xlsx?${qs}`, { asFile: true });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <h3 style={s.h3}>Reporte de casos</h3>
      <div style={s.filters}>
        <select style={s.date} value={userId} onChange={e => setUserId(e.target.value)} aria-label="Asesor">
          <option value="">Todos los asesores</option>
          {people.map(u => <option key={u.id} value={u.id}>{u.name}{u.is_active ? '' : ' (inactivo)'}</option>)}
        </select>
        <select style={s.date} value={tagId} onChange={e => setTagId(e.target.value)} aria-label="Etiqueta">
          <option value="">Todas las etiquetas</option>
          {tags.map(t => <option key={t.id} value={t.id}>{t.emoji ? `${t.emoji} ` : ''}{t.name}</option>)}
        </select>
        <button style={{ ...s.chip, ...s.chipActive }} onClick={download} disabled={busy}>
          {busy ? 'Generando…' : '⬇ Descargar Excel'}
        </button>
        <span style={s.muted}>Una fila por caso que empieza entre {desde} y {hasta}. Sin el texto de los mensajes.</span>
      </div>
      {error && <div style={{ ...s.errorBox, marginTop: 8 }}>⚠️ {error}</div>}
    </section>
  );
}

/**
 * Métricas de atención: indicadores del período, foto actual y detalle por asesor.
 */
export default function MetricsPanel() {
  const { apiFetch } = useApi();
  const [range, setRange] = useState({ desde: dayStr(-6), hasta: dayStr(0), preset: '7d' });
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await apiFetch(`/api/admin/metrics?desde=${range.desde}&hasta=${range.hasta}`));
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [apiFetch, range.desde, range.hasta]);

  useEffect(() => { load(); }, [load]);

  const sm = data?.summary;
  const q = data?.queue_time;
  const fr = data?.first_response;

  return (
    <div style={s.wrap}>
      {/* Filtros en una sola fila, arriba */}
      <div style={s.filters}>
        {PRESETS.map(p => (
          <button key={p.key} style={{ ...s.chip, ...(range.preset === p.key ? s.chipActive : {}) }}
            onClick={() => setRange({ desde: p.desde(), hasta: dayStr(0), preset: p.key })}>{p.label}</button>
        ))}
        <input type="date" style={s.date} value={range.desde} max={range.hasta}
          onChange={e => setRange(r => ({ ...r, desde: e.target.value, preset: null }))} />
        <span style={s.muted}>a</span>
        <input type="date" style={s.date} value={range.hasta} min={range.desde}
          onChange={e => setRange(r => ({ ...r, hasta: e.target.value, preset: null }))} />
        <button style={s.chip} onClick={load} disabled={loading}>{loading ? 'Cargando…' : '↻ Actualizar'}</button>
        <span style={s.muted}>Días de Colombia</span>
      </div>

      {error && <div style={s.errorBox}>⚠️ {error}</div>}

      <CaseReport desde={range.desde} hasta={range.hasta} />

      {data && (
        <>
          <section>
            <h3 style={s.h3}>Ahora mismo</h3>
            <div style={s.tiles}>
              <Tile label="En cola" value={data.now.queued}
                caption={data.now.queued ? `el más antiguo espera ${dur(data.now.oldest_queued_s)}` : 'nadie esperando'} />
              <Tile label="Casos abiertos" value={data.now.assigned} caption="asignados a un asesor" />
              <Tile label="Asesores disponibles" value={data.now.online} caption={`${data.now.paused} en pausa`} />
            </div>
          </section>

          <section>
            <h3 style={s.h3}>En el período</h3>
            <div style={s.tiles}>
              <Tile label="Escalados por SARA" value={sm.escalated} caption={`${sm.assigned} asignaciones (${sm.assigned_auto} automáticas)`} />
              <Tile label="Tiempo en cola" value={dur(q.avg_s)} caption={q.n ? `mediana ${dur(q.p50_s)} · 90 % bajo ${dur(q.p90_s)}` : 'sin casos en cola'}
                note={q.not_assigned ? `${q.not_assigned} sin asignar aún` : null} />
              <Tile label="Primera respuesta" value={dur(fr.avg_s)} caption={fr.n ? `mediana ${dur(fr.p50_s)} · 90 % bajo ${dur(fr.p90_s)}` : 'sin respuestas'}
                note={fr.no_response ? `${fr.no_response} de ${fr.cases} casos sin respuesta` : null} />
              <Tile label="Cierres" value={sm.closed_manual + sm.closed_inactivity}
                caption={`${sm.closed_manual} manuales · ${sm.closed_inactivity} por inactividad`}
                note={sm.requeued ? `${sm.requeued} reencolados (desconexión o sin "Recibe casos")` : null} />
            </div>
          </section>

          <section>
            <h3 style={s.h3}>Por asesor</h3>
            <div style={s.tableWrap}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Asesor</th>
                    <th style={s.thNum}>Casos</th>
                    <th style={s.thNum}>1.ª respuesta (prom.)</th>
                    <th style={s.thNum}>Sin respuesta</th>
                    <th style={s.thNum}>Cerrados</th>
                    <th style={s.thNum}>Cerrados por inactividad</th>
                    <th style={s.thNum}>Reencolados</th>
                    <th style={s.thNum}>Mensajes</th>
                    <th style={s.thNum}>Plantillas</th>
                    <th style={s.thNum}>Envíos fallidos</th>
                  </tr>
                </thead>
                <tbody>
                  {data.agents.length === 0 && <tr><td style={s.td} colSpan={10}>Sin actividad en el período.</td></tr>}
                  {data.agents.map(a => (
                    <tr key={a.id} style={{ opacity: a.is_active ? 1 : 0.55 }}>
                      <td style={s.td}>{a.name}{a.role === 'admin' ? <span style={s.muted}> · admin</span> : ''}{!a.is_active ? <span style={s.muted}> · inactivo</span> : ''}</td>
                      <td style={s.tdNum}>{a.cases}</td>
                      <td style={s.tdNum}>{dur(a.first_response?.avg_s)}</td>
                      <td style={s.tdNum}>{a.first_response?.no_response ?? 0}</td>
                      <td style={s.tdNum}>{a.closed}</td>
                      <td style={s.tdNum}>{a.closed_inactivity}</td>
                      <td style={s.tdNum}>{a.requeued}</td>
                      <td style={s.tdNum}>{a.messages}</td>
                      <td style={s.tdNum}>{a.templates}</td>
                      <td style={s.tdNum}>{a.failed ? <span title="Envíos que Meta rechazó">⚠ {a.failed}</span> : 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ ...s.muted, marginTop: 8 }}>
              Tiempo en cola: desde que el caso entra a la cola hasta que se asigna. Primera respuesta: desde la asignación
              hasta el primer mensaje enviado por esa persona, antes de que el caso cambie de manos.
            </div>
          </section>
        </>
      )}
    </div>
  );
}

/** Indicador: rótulo, cifra y aclaración (las cifras van en tinta de texto, no en color). */
function Tile({ label, value, caption, note }) {
  return (
    <div style={s.tile}>
      <div style={s.tileLabel}>{label}</div>
      <div style={s.tileValue}>{value ?? '—'}</div>
      <div style={s.tileCaption}>{caption}</div>
      {note && <div style={s.tileNote}>{note}</div>}
    </div>
  );
}

const s = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 20 },
  filters: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  chip: { background: 'var(--surface-2)', border: '1px solid var(--border-strong)', borderRadius: 20, padding: '5px 12px', fontSize: 12, color: 'var(--text-2)', cursor: 'pointer' },
  chipActive: { background: 'var(--accent-soft)', border: '1px solid var(--accent-border)', color: 'var(--text)' },
  date: { background: 'var(--surface-2)', border: '1px solid var(--border-strong)', borderRadius: 8, padding: '5px 8px', color: 'var(--text)', fontSize: 12, colorScheme: 'dark' },
  muted: { color: 'var(--text-3)', fontSize: 12 },
  h3: { fontSize: 13, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 0.8, margin: '0 0 10px' },
  tiles: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 12 },
  tile: { background: 'var(--surface)', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 4 },
  tileLabel: { fontSize: 12, color: 'var(--text-2)', fontWeight: 600 },
  tileValue: { fontSize: 28, fontWeight: 800, color: 'var(--text)', fontVariantNumeric: 'tabular-nums', lineHeight: 1.15 },
  tileCaption: { fontSize: 12, color: 'var(--text-2)' },
  tileNote: { fontSize: 12, color: 'var(--text-2)', marginTop: 2 },
  errorBox: { background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', borderRadius: 10, padding: '10px 14px', color: 'var(--danger)', fontSize: 13 },
  tableWrap: { overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 14, background: 'var(--surface)', boxShadow: 'var(--shadow-sm)' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 12px', color: 'var(--text-2)', fontSize: 11, fontWeight: 600, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' },
  thNum: { textAlign: 'right', padding: '10px 12px', color: 'var(--text-2)', fontSize: 11, fontWeight: 600, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' },
  td: { padding: '9px 12px', borderBottom: '1px solid var(--border)', color: 'var(--text)' },
  tdNum: { padding: '9px 12px', borderBottom: '1px solid var(--border)', color: 'var(--text)', textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
};
